import Navbar from "../components/Navbar";
import { useEffect, useState } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

// 🏟️ Team Logos Mapping
import CSK from "../logos/CSK.png";
import DC from "../logos/DC.png";
import GT from "../logos/GT.png";
import KKR from "../logos/KKR.png";
import LSG from "../logos/LSG.png";
import MI from "../logos/MI.png";
import PBKS from "../logos/PBKS.png";
import RCB from "../logos/RCB.png";
import RR from "../logos/RR.png";
import SRH from "../logos/SRH.png";

const teamLogos = { CSK, DC, GT, KKR, LSG, MI, PBKS, RCB, RR, SRH };

function MyPredictions() {
  const [myPicks, setMyPicks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArenaData = async (user) => {
      try {
        // 🛡️ 1. Fetch User Picks
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          setLoading(false);
          return;
        }

        const userPredictions = userSnap.data().predictions || [];
        
        // 🛡️ 2. Fetch All Matches from Firestore (More reliable than static)
        const matchSnap = await getDocs(collection(db, "matches"));
        const allMatches = matchSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const joinedData = userPredictions.map(p => {
          const match = allMatches.find(m => m.id.toString() === p.matchId.toString());
          return match ? { ...match, prediction: p.team, predictedAt: p.timestamp } : null;
        }).filter(x => x !== null);

        setMyPicks(joinedData.sort((a,b) => new Date(b.predictedAt) - new Date(a.predictedAt)));
      } catch (err) {
        console.error("Failed to load picks:", err);
      } finally {
        setLoading(false);
      }
    };

    const unsub = auth.onAuthStateChanged((u) => {
      if (u) fetchArenaData(u);
      else setLoading(false);
    });

    return () => unsub();
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: 'var(--stadium-gradient)' }}>
      <Navbar />
      <div className="app-container">
        <header style={{marginBottom: '3rem'}}>
           <h1 className="gradient-text" style={{fontSize: '3rem'}}>MY VAULT</h1>
           <p style={{color: 'var(--text-secondary)'}}>Your tactical decisions, secured in the IPL Arena.</p>
        </header>

        {loading ? (
           <div style={styles.loader}>Syncing with Command Center...</div>
        ) : myPicks.length === 0 ? (
          <div className="glass-morphism animate-fade-in" style={styles.emptyCard}>
            <div style={{fontSize: '4rem', marginBottom: '1rem'}}>📊</div>
            <h3 style={{color: 'var(--text-primary)', fontSize: '1.5rem'}}>No Picks Detected</h3>
            <p style={{marginTop: '0.5rem'}}>Head back to the Arena and cast your first prediction!</p>
          </div>
        ) : (
          <div style={styles.grid}>
            {myPicks.map((match, idx) => (
              <div key={idx} className="glass-morphism animate-fade-in" style={styles.card}>
                <div style={styles.matchHeader}>
                   <span style={styles.dateBadge}>{match.date}</span>
                   <span style={{...styles.statusBadge, 
                       color: match.status === 'finished' ? (match.prediction === match.winner ? '#10b981' : '#ef4444') : 'var(--primary)',
                       background: match.status === 'finished' ? (match.prediction === match.winner ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)') : 'rgba(0,118,255,0.1)'
                   }}>
                      {match.status === 'finished' ? (match.prediction === match.winner ? 'CORRECT' : 'INCORRECT') : 'PENDING'}
                   </span>
                </div>

                <div style={styles.matchVisual}>
                   <div style={styles.teamLine}>
                      <img src={teamLogos[match.teamA]} alt="" style={styles.tinyLogo} />
                      <span style={{fontWeight: '700'}}>{match.teamA}</span>
                   </div>
                   <div style={styles.vsLine}>VS</div>
                   <div style={styles.teamLine}>
                      <span style={{fontWeight: '700'}}>{match.teamB}</span>
                      <img src={teamLogos[match.teamB]} alt="" style={styles.tinyLogo} />
                   </div>
                </div>

                <div style={styles.pickDetails}>
                   <span style={styles.pickLabel}>YOUR SELECTION</span>
                   <div style={styles.pickDisplay}>
                      <img src={teamLogos[match.prediction]} alt="" style={styles.pickLogo} />
                      <span style={styles.pickTeam}>{match.prediction}</span>
                   </div>
                </div>

                <div style={styles.cardFooter}>
                   <span style={styles.venue}>{match.venue}</span>
                   <span style={styles.timestamp}>Saved {new Date(match.predictedAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem', paddingBottom: '3rem' },
  card: { padding: '1.5rem', borderRadius: '24px', position: 'relative', overflow: 'hidden' },
  matchHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' },
  dateBadge: { fontSize: '0.7rem', fontWeight: '800', opacity: 0.6, letterSpacing: '0.1em' },
  statusBadge: { fontSize: '0.6rem', fontWeight: '900', padding: '0.3rem 0.8rem', borderRadius: '50px' },
  matchVisual: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', background: 'rgba(255,255,255,0.02)', padding: '0.8rem', borderRadius: '16px' },
  teamLine: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' },
  tinyLogo: { width: '28px', height: '28px', objectFit: 'contain' },
  vsLine: { fontSize: '0.7rem', fontWeight: '900', opacity: 0.3 },
  pickDetails: { textAlign: 'center', margin: '1rem 0', padding: '1.2rem', background: 'var(--bg-secondary)', borderRadius: '16px', border: '1px solid var(--glass-border)' },
  pickLabel: { fontSize: '0.6rem', fontWeight: '800', color: 'var(--text-secondary)', letterSpacing: '0.1em' },
  pickDisplay: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.8rem', marginTop: '0.5rem' },
  pickLogo: { width: '40px', height: '40px', objectFit: 'contain' },
  pickTeam: { fontSize: '1.4rem', fontWeight: '900', color: 'var(--primary)', letterSpacing: '0.05em' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid var(--glass-border)', fontSize: '0.65rem', fontWeight: '700', color: 'var(--text-secondary)' },
  venue: { textTransform: 'uppercase' },
  emptyCard: { padding: '5rem 2rem', borderRadius: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  loader: { textAlign: 'center', padding: '10rem', fontSize: '1.2rem', fontWeight: '800', color: 'var(--primary)' }
};

export default MyPredictions;